/**
 * Tutor assignment detail tab.
 * Current wishes / latest status, manage-assignment CTA, and attempt history.
 */
import * as React from 'react';
import { Building2, ClipboardCheck, ClipboardList, Clock } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { type RemoteStatusConfig } from '@/components/tutors/tutor-configs';
import {
  getTutorAssignmentStatusColor,
  getTutorAssignmentStatusLabel,
} from '@/components/tutors/inperson/tutor-form-utils';
import { toast } from '@/components/ui/toast';
import { usePageActions } from '@/hooks/usePageActions';
import { useInPersonTutorAssignments } from '@/hooks/useUsers';
import { useEstablishmentsByIds } from '@/hooks/useEstablishments';
import { AssignmentDecisionModal } from '@/components/tutors/inperson/AssignmentDecisionModal';
import {
  formatTutorAssignmentActorName,
  formatTutorShortDate,
  getTutorAssignmentEstablishmentName,
  getTutorWishEstablishmentDisplay,
  getTutorWishListKey,
  mapTutorAssignmentWishesWithNames,
  type TutorContentDisplay,
} from '@/components/tutors/inperson/tutor-table-utils';

/** Chronological assignment attempts (status, actors, refusal). */
function TutorAssignmentHistory({
  isLoading,
  assignments,
}: Readonly<{
  isLoading: boolean;
  assignments?: Array<{
    id?: string;
    status?: string;
    isDirectAssignment?: boolean;
    establishment?: { name?: string };
    establishmentName?: string;
    createdAt?: string;
    proposedAt?: string;
    validatedAt?: string;
    refusedAt?: string;
    proposedBy?: { firstName?: string; lastName?: string; userFullName?: string };
    validatedBy?: { firstName?: string; lastName?: string; userFullName?: string };
    refusedBy?: { firstName?: string; lastName?: string; userFullName?: string };
    refusalReason?: string;
    comments?: string;
    reassignmentCount?: number;
  }>;
}>) {
  if (isLoading) return <Spinner size="sm" />;
  if (!assignments?.length) {
    return <p className="text-sm text-muted-foreground">Aucune tentative d'affectation</p>;
  }
  return (
    <div className="space-y-3">
      {assignments.map((aff) => (
        <div key={aff.id} className="p-3 border rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{getTutorAssignmentEstablishmentName(aff)}</span>
            <div className="flex items-center gap-2">
              {aff.isDirectAssignment && (
                <LabelBadge
                  label="Direct"
                  variant="outlined"
                  color="rgb(var(--color-3-nuance-1))"
                  size="sm"
                />
              )}
              <LabelBadge
                label={getTutorAssignmentStatusLabel(aff.status)}
                variant="outlined"
                color={getTutorAssignmentStatusColor(aff.status)}
                size="sm"
              />
            </div>
          </div>
          <div className="text-xs text-muted-foreground space-y-0.5">
            <div>Créé le {formatTutorShortDate(aff.createdAt)}</div>
            {aff.proposedBy && (
              <div>
                Proposé par {formatTutorAssignmentActorName(aff.proposedBy)} le{' '}
                {formatTutorShortDate(aff.proposedAt ?? aff.createdAt)}
              </div>
            )}
            {aff.validatedBy && (
              <div>
                Validé par {formatTutorAssignmentActorName(aff.validatedBy)} le{' '}
                {formatTutorShortDate(aff.validatedAt)}
              </div>
            )}
            {aff.refusedBy && (
              <div>
                Refusé par {formatTutorAssignmentActorName(aff.refusedBy)} le{' '}
                {formatTutorShortDate(aff.refusedAt)}
              </div>
            )}
            {aff.refusalReason && <div className="font-medium">Motif : {aff.refusalReason}</div>}
            {aff.comments && <div>Commentaire : {aff.comments}</div>}
            {(aff.reassignmentCount ?? 0) > 0 && (
              <div>Tentative n°{(aff.reassignmentCount ?? 0) + 1}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Props for {@link TutorAssignmentTab}. */
interface Props {
  display: TutorContentDisplay;
  situationConfig: RemoteStatusConfig;
}

/** Tutor assignment detail tab. */
export const TutorAssignmentTab: React.FC<Props> = ({ display, situationConfig }) => {
  const { user: authUser } = useAuthStore();
  const companyId = authUser?.companyId ?? '';
  const pageActions = usePageActions('tutors');
  const { userId, departments, wishes, wishEstabIds } = display;
  const SituationIcon = situationConfig.icon;

  const { data: assignments, isLoading: isLoadingAssignments } = useInPersonTutorAssignments(
    userId,
    companyId,
  );
  const { isLoading: isLoadingWishEstabs, establishments: wishEstabs } =
    useEstablishmentsByIds(wishEstabIds);

  /** Open {@link AssignmentDecisionModal} for pre-validate or validate. */
  const [assignmentModal, setAssignmentModal] = React.useState<{
    open: boolean;
    mode: 'pre-validate' | 'validate';
  } | null>(null);

  if (isLoadingAssignments || isLoadingWishEstabs) {
    return (
      <div className="col-span-2 flex justify-center py-12">
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <>
      <DetailsCard icon={Building2} title="Affectation" colorName="color-2" colSpan={2}>
        <div className="space-y-3">
          <LabelBadge
            label={situationConfig.label}
            icon={SituationIcon}
            variant={situationConfig.badgeVariant}
            color={situationConfig.badgeColor}
            size="md"
          />

          <div className="pt-2 space-y-2">
            <h4 className="text-sm font-medium">Établissements souhaités</h4>
            {wishes.length > 0 ? (
              <TooltipProvider>
                <ul className="space-y-1">
                  {wishes.map((wish) => {
                    const estab = wish.establishmentId
                      ? wishEstabs[wish.establishmentId]
                      : undefined;
                    const { name, ratio, managedByName, hasEstab } =
                      getTutorWishEstablishmentDisplay(wish, estab);
                    return (
                      <li key={getTutorWishListKey(wish)} className="text-sm">
                        •{' '}
                        {hasEstab ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-default underline decoration-dotted underline-offset-2">
                                {name}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent variant="light" className="space-y-1">
                              {ratio && <p className="text-xs font-medium">Tuteurs : {ratio}</p>}
                              <p className="text-xs font-medium">
                                Géré par : {managedByName || '—'}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span>{name}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </TooltipProvider>
            ) : (
              <p className="text-sm text-muted-foreground">Aucun établissement souhaité</p>
            )}

            {assignments && assignments.length > 0 && assignments[0].status === 'validated' && (
              <div className="pt-2 border-t">
                {assignments[0].isDirectAssignment ? (
                  <p className="text-xs text-muted-foreground">
                    Affecté directement le {formatTutorShortDate(assignments[0].validatedAt)} par{' '}
                    {formatTutorAssignmentActorName(assignments[0].validatedBy)}
                  </p>
                ) : (
                  <>
                    {assignments[0].proposedBy && (
                      <p className="text-xs text-muted-foreground">
                        Prévalidé le{' '}
                        {formatTutorShortDate(
                          assignments[0].proposedAt ?? assignments[0].createdAt,
                        )}{' '}
                        par {formatTutorAssignmentActorName(assignments[0].proposedBy)}
                      </p>
                    )}
                    {assignments[0].validatedBy && (
                      <p className="text-xs text-muted-foreground">
                        Validé le {formatTutorShortDate(assignments[0].validatedAt)} par{' '}
                        {formatTutorAssignmentActorName(assignments[0].validatedBy)}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {assignments &&
              assignments.length > 0 &&
              assignments[0].status === 'awaiting_coordinator_validation' &&
              assignments[0].proposedBy && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    Prévalidé le{' '}
                    {formatTutorShortDate(assignments[0].proposedAt ?? assignments[0].createdAt)}{' '}
                    par {formatTutorAssignmentActorName(assignments[0].proposedBy)} — en attente de
                    validation coordinateur
                  </p>
                </div>
              )}

            {assignments &&
              assignments.length > 0 &&
              (assignments[0].status === 'refused_by_recruiter' ||
                assignments[0].status === 'refused_by_coordinator') &&
              assignments[0].refusedBy && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    Refusé le {formatTutorShortDate(assignments[0].refusedAt)} par{' '}
                    {assignments[0].refusedBy.firstName} {assignments[0].refusedBy.lastName}
                  </p>
                  {assignments[0].refusalReason && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Motif : {assignments[0].refusalReason}
                    </p>
                  )}
                </div>
              )}
          </div>

          <div className="flex gap-2 pt-3 border-t">
            {pageActions.canPrevalidate && pageActions.canValidateAssignment ? (
              <Button
                variant="primary"
                size="sm"
                icon={ClipboardCheck}
                disabled={isLoadingAssignments || (!assignments?.length && !wishEstabIds.length)}
                onClick={() => setAssignmentModal({ mode: 'validate', open: true })}
              >
                Gérer les affectations
              </Button>
            ) : (
              <>
                {pageActions.canPrevalidate && (
                  <Button
                    variant="outline"
                    size="sm"
                    icon={ClipboardList}
                    disabled={
                      isLoadingAssignments || (!assignments?.length && !wishEstabIds.length)
                    }
                    onClick={() => setAssignmentModal({ mode: 'pre-validate', open: true })}
                  >
                    Gérer les affectations
                  </Button>
                )}
                {pageActions.canValidateAssignment && (
                  <Button
                    variant="primary"
                    size="sm"
                    icon={ClipboardCheck}
                    disabled={isLoadingAssignments || !assignments?.length}
                    onClick={() => setAssignmentModal({ mode: 'validate', open: true })}
                  >
                    Gérer les affectations
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </DetailsCard>

      <DetailsCard icon={Clock} title="Historique des tentatives" colorName="color-3" colSpan={2}>
        <TutorAssignmentHistory isLoading={isLoadingAssignments} assignments={assignments} />
      </DetailsCard>

      {assignmentModal && (
        <AssignmentDecisionModal
          open={assignmentModal.open}
          onClose={() => setAssignmentModal(null)}
          mode={assignmentModal.mode}
          userId={userId}
          companyId={companyId}
          departmentLabel={departments.join(', ')}
          showBothSections={
            pageActions.canPrevalidate === true && pageActions.canValidateAssignment === true
          }
          isLoadingNames={isLoadingWishEstabs}
          wishes={mapTutorAssignmentWishesWithNames(wishes, wishEstabs)}
          onSuccess={() => {
            toast.success('Affectation mise à jour avec succès');
            setAssignmentModal(null);
            window.location.reload();
          }}
        />
      )}
    </>
  );
};
